import React, { useState, useEffect } from 'react';
import { Line, Bar, Pie } from 'react-chartjs-2';
import axios from 'axios';
import GLOBALS from '../../utils/Globals';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const COLORS = [
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 99, 132, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(255, 206, 86, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(255, 159, 64, 0.7)',
];

const cardStyle = {
  backgroundColor: 'white',
  padding: '15px',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const statCardStyle = {
  ...cardStyle,
  textAlign: 'center',
};

const PublishingAnalytics = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const url =
          process.env.REACT_APP_ENV === 'PROD'
            ? GLOBALS.PROD.baseURLGetPublishedReports
            : GLOBALS.DEV.baseURLGetPublishedReports;
        const response = await axios.get(url);
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setReports(data);
      } catch (err) {
        console.error('Error fetching publish reports:', err);
        setError('Failed to load publishing analytics.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        Loading publishing analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
        {error}
      </div>
    );
  }

  // ── Summary stats ──────────────────────────────────────────────────────────
  const total = reports.length;
  const successCount = reports.filter(r => r.status === 'success').length;
  const successRate = total > 0 ? ((successCount / total) * 100).toFixed(1) : 0;
  const coverages = reports
    .map(r => r.mapping_statistics?.mapping_percentage)
    .filter(v => v != null);
  const avgCoverage = coverages.length
    ? (coverages.reduce((a, b) => a + b, 0) / coverages.length).toFixed(1)
    : 0;
  const times = reports
    .map(r => r.mapping_statistics?.processing_time_seconds)
    .filter(v => v != null);
  const avgTime = times.length
    ? (times.reduce((a, b) => a + b, 0) / times.length).toFixed(2)
    : 0;

  // ── Chart 1: Success vs Fail by day ───────────────────────────────────────
  const byDay = reports.reduce((acc, r) => {
    const day = r.created_at ? new Date(r.created_at).toLocaleDateString() : 'Unknown';
    if (!acc[day]) acc[day] = { success: 0, fail: 0 };
    if (r.status === 'success') acc[day].success++;
    else acc[day].fail++;
    return acc;
  }, {});
  const dayLabels = Object.keys(byDay).sort((a, b) => new Date(a) - new Date(b));
  const successFailData = {
    labels: dayLabels,
    datasets: [
      { label: 'Success', data: dayLabels.map(d => byDay[d].success), backgroundColor: 'rgba(75,192,192,0.7)', borderColor: 'rgba(75,192,192,1)', borderWidth: 1 },
      { label: 'Failed', data: dayLabels.map(d => byDay[d].fail), backgroundColor: 'rgba(255,99,132,0.7)', borderColor: 'rgba(255,99,132,1)', borderWidth: 1 },
    ],
  };

  // ── Chart 2: Publishes by customer ────────────────────────────────────────
  const byCustomer = reports.reduce((acc, r) => {
    const c = r.customer || 'Unknown';
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});
  const customerLabels = Object.keys(byCustomer);
  const customerData = {
    labels: customerLabels,
    datasets: [{ label: 'Publishes', data: customerLabels.map(c => byCustomer[c]), backgroundColor: COLORS, borderWidth: 1 }],
  };

  // ── Chart 3: Avg mapping coverage % by day ────────────────────────────────
  const coverageByDay = reports.reduce((acc, r) => {
    const day = r.created_at ? new Date(r.created_at).toLocaleDateString() : 'Unknown';
    const pct = r.mapping_statistics?.mapping_percentage;
    if (pct != null) {
      if (!acc[day]) acc[day] = { sum: 0, count: 0 };
      acc[day].sum += pct;
      acc[day].count++;
    }
    return acc;
  }, {});
  const coverageDayLabels = Object.keys(coverageByDay).sort((a, b) => new Date(a) - new Date(b));
  const coverageData = {
    labels: coverageDayLabels,
    datasets: [{ label: 'Avg Coverage %', data: coverageDayLabels.map(d => parseFloat((coverageByDay[d].sum / coverageByDay[d].count).toFixed(1))), fill: false, borderColor: 'rgba(153,102,255,1)', backgroundColor: 'rgba(153,102,255,0.2)', tension: 0.3 }],
  };

  // ── Chart 4: Avg processing time by day ───────────────────────────────────
  const timeByDay = reports.reduce((acc, r) => {
    const day = r.created_at ? new Date(r.created_at).toLocaleDateString() : 'Unknown';
    const t = r.mapping_statistics?.processing_time_seconds;
    if (t != null) {
      if (!acc[day]) acc[day] = { sum: 0, count: 0 };
      acc[day].sum += t;
      acc[day].count++;
    }
    return acc;
  }, {});
  const timeDayLabels = Object.keys(timeByDay).sort((a, b) => new Date(a) - new Date(b));
  const processingTimeData = {
    labels: timeDayLabels,
    datasets: [{ label: 'Avg Processing Time (s)', data: timeDayLabels.map(d => parseFloat((timeByDay[d].sum / timeByDay[d].count).toFixed(2))), backgroundColor: 'rgba(255,159,64,0.7)', borderColor: 'rgba(255,159,64,1)', borderWidth: 1 }],
  };

  const chartOptions = (yLabel) => ({
    responsive: true,
    plugins: { legend: { position: 'top' } },
    scales: { y: { beginAtZero: true, title: { display: true, text: yLabel } } },
  });

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ ...cardStyle, textAlign: 'center', marginBottom: '24px' }}>
        <h2 style={{ color: '#333', marginBottom: '8px' }}>Publishing Analytics</h2>
        <p style={{ color: '#666', margin: 0 }}>Insights from {total} publish report{total !== 1 ? 's' : ''}</p>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={statCardStyle}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>{total}</div>
          <div style={{ color: '#888', marginTop: '4px' }}>Total Publishes</div>
        </div>
        <div style={statCardStyle}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: successRate >= 90 ? '#52c41a' : successRate >= 70 ? '#fa8c16' : '#f5222d' }}>{successRate}%</div>
          <div style={{ color: '#888', marginTop: '4px' }}>Success Rate</div>
        </div>
        <div style={statCardStyle}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: avgCoverage >= 90 ? '#52c41a' : '#fa8c16' }}>{avgCoverage}%</div>
          <div style={{ color: '#888', marginTop: '4px' }}>Avg Mapping Coverage</div>
        </div>
        <div style={statCardStyle}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1890ff' }}>{avgTime}s</div>
          <div style={{ color: '#888', marginTop: '4px' }}>Avg Processing Time</div>
        </div>
      </div>

      {/* Charts — top row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px', marginBottom: '20px' }}>
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '12px', color: '#555' }}>Publish Success vs Fail by Day</h3>
          <Bar data={successFailData} options={chartOptions('Count')} />
        </div>
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '12px', color: '#555' }}>Publishes by Customer</h3>
          <Pie data={customerData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
        </div>
      </div>

      {/* Charts — bottom row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '12px', color: '#555' }}>Avg Mapping Coverage % Over Time</h3>
          <Line data={coverageData} options={chartOptions('Coverage %')} />
        </div>
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '12px', color: '#555' }}>Avg Processing Time (s) by Day</h3>
          <Bar data={processingTimeData} options={chartOptions('Seconds')} />
        </div>
      </div>
    </div>
  );
};

export default PublishingAnalytics;

